import { useCallback, useEffect, useRef, useState } from "react";
import type { Contact, MailboxOrder, OrderState } from "../../../shared/mailboxOrders";
import { Icon } from "../../components/ui/Icon";
import { shownError } from "../../lib/connectionsApi";
import { IN_APP } from "../../lib/inApp";
import { MOVING, STATE_WORDS, mailboxOrdersApi, priceShort, vendorTitle } from "../../lib/mailboxOrdersApi";
import { BuyMailboxes } from "./BuyMailboxes";
import "../../styles/mailbox-orders.css";

const POLL_MS = 30_000;

interface Props {
  productId: string;
  productName: string;
  say: (message: string, done?: boolean) => void;
  /** An order's mailboxes arrived: the connection's own checks change with them. */
  onReady: () => void;
}

/** A product's mailbox orders, read again while one is on its way, and the draft's Cancel. */
export function useMailboxOrders(productId: string, say: (message: string, done?: boolean) => void, onReady: () => void) {
  const [data, setData] = useState<{ orders: MailboxOrder[]; contact: Contact | null } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const seen = useRef<Map<string, OrderState> | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await mailboxOrdersApi.list(productId);
      const before = seen.current;
      seen.current = new Map(r.orders.map((o) => [o.id, o.state]));
      if (before && r.orders.some((o) => o.state === "ready" && before.get(o.id) !== "ready")) onReady();
      setData(r);
      setErr(null);
    } catch (e) {
      setErr(shownError(e));
    }
  }, [productId, onReady]);

  useEffect(() => {
    void load();
  }, [load]);

  const moving = !!data?.orders.some((o) => MOVING.includes(o.state));
  useEffect(() => {
    if (!moving) return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [moving, load]);

  const cancel = async (o: MailboxOrder) => {
    setBusy(o.id);
    try {
      await mailboxOrdersApi.cancel(productId, o.id);
      say("Draft cancelled.");
    } catch (e) {
      say(shownError(e), false);
    } finally {
      setBusy(null);
      await load();
    }
  };

  return { data, err, busy, load, cancel };
}

/**
 * Under the cold email connection: the domains and mailboxes bought through Smartlead, and the way to buy more.
 * Inside the Flashsuite phone app nothing is bought (APP-MODE.md section 2): the domains show without prices, and
 * a draft can only be cancelled.
 */
export function MailboxOrders({ productId, productName, say, onReady }: Props) {
  const { data, err, busy, load, cancel } = useMailboxOrders(productId, say, onReady);
  const [buying, setBuying] = useState(false);
  const [draft, setDraft] = useState<MailboxOrder | null>(null);
  const [showCancelled, setShowCancelled] = useState(false);

  const orders = data?.orders ?? [];
  const cancelled = orders.filter((o) => o.state === "cancelled");
  const shown = orders.filter((o) => o.state !== "cancelled" || showCancelled);
  if (IN_APP && orders.length === 0 && !err) return null;

  return (
    <div className="mo" id="mo">
      <div className="mo-h">
        <b>Sending domains</b>
        {IN_APP ? null : (
          <button type="button" className="chip link" onClick={() => setBuying(true)}>
            <Icon name="plus" />
            Buy a domain and sending addresses
          </button>
        )}
      </div>
      {err && <p className="cx-err">{err}</p>}
      {data && orders.length === 0 && <p className="mo-none">Buy a separate domain with its sending addresses here. Smartlead sets them up and charges the card on your Smartlead account.</p>}
      {shown.length > 0 && (
        <ul className="mo-list">
          {shown.map((o) => {
            const st = STATE_WORDS[o.state];
            return (
              <li key={o.id} className="mo-o" id={`mo-${o.id}`}>
                <div className="mo-oh">
                  <b>{o.domains.map((d) => d.domain).join(", ")}</b>
                  <span className={`pill ${st.pill}`}>{st.words}</span>
                </div>
                <p className="mo-mb">{o.domains.flatMap((d) => d.mailboxes.map((m) => m.address)).join(", ")}</p>
                <p className="mo-meta">
                  {[vendorTitle({ provider: o.provider, name: o.vendorName }), IN_APP ? null : priceShort(o), `leads to ${o.forwardingDomain}`].filter(Boolean).join(" · ")}
                </p>
                {o.note && <p className="mo-note">{o.note}</p>}
                {o.state === "draft" && (
                  <div className="mo-act">
                    {IN_APP ? null : (
                      <button type="button" className="btn sm" disabled={busy === o.id} onClick={() => setDraft(o)}>
                        Buy
                      </button>
                    )}
                    <button type="button" className="btn sm ghost" disabled={busy === o.id} onClick={() => void cancel(o)}>
                      Cancel
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {cancelled.length > 0 && (
        <button type="button" className="wk-undo mo-more" onClick={() => setShowCancelled((v) => !v)}>
          {showCancelled ? "Hide cancelled drafts" : `Show ${cancelled.length} cancelled ${cancelled.length === 1 ? "draft" : "drafts"}`}
        </button>
      )}
      {IN_APP ? null : (
        <BuyMailboxes
          productId={productId}
          productName={productName}
          open={buying}
          draft={draft}
          orders={orders}
          lastContact={data?.contact ?? null}
          onClose={() => {
            setBuying(false);
            setDraft(null);
          }}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
}
